import { useState } from 'react';
import type { CSSProperties } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';

import { TapField } from '#components/mobile/MobileForms';
import { useFormat } from '#hooks/useFormat';

import { Boton } from './Boton';
import { Hoja } from './Hoja';
import { TecladoImporte, useImporteTecleado } from './TecladoImporte';
import { color, densidad, num, radio, texto } from './tokens';

type CampoImporteProps = {
  /** Fila de formulario con etiqueta (como `InputField etiqueta`). */
  etiqueta?: string;
  /** Sin etiqueta visible: nombre del campo y título de la hoja. */
  'aria-label'?: string;
  /** Céntimos; `null` = vacío. */
  valor: number | null;
  onCambiar: (centimos: number | null) => void;
  /** Se puede dejar vacío (`null`), p. ej. «igual que la nómina». */
  opcional?: boolean;
  /** Admite negativos (saldos en descubierto): botón «−/+» en la hoja. */
  conSigno?: boolean;
  placeholder?: string;
  isDisabled?: boolean;
  id?: string;
  'data-testid'?: string;
  /** Caja del campo sin etiqueta. */
  style?: CSSProperties;
  /** Texto del importe del campo sin etiqueta. */
  estiloTexto?: CSSProperties;
};

/**
 * Campo de importe con el teclado de Rumbo (regla fija: ningún importe abre
 * el teclado del móvil). Al tocarlo sube una hoja con la cifra y el teclado;
 * «Hecho» guarda y «atrás» o tocar fuera la cierra sin cambiar nada.
 */
export function CampoImporte({
  etiqueta,
  'aria-label': ariaLabel,
  valor,
  onCambiar,
  opcional = false,
  conSigno = false,
  placeholder,
  isDisabled,
  id,
  'data-testid': testId,
  style,
  estiloTexto,
}: CampoImporteProps) {
  const format = useFormat();
  const [abierto, setAbierto] = useState(false);
  const vacio = valor == null || (valor === 0 && placeholder != null);
  const visible = vacio
    ? (placeholder ?? format(0, 'financial'))
    : format(valor ?? 0, 'financial');
  const nombre = etiqueta ?? ariaLabel ?? '';

  return (
    <>
      {etiqueta ? (
        <TapField
          id={id}
          etiqueta={etiqueta}
          value={vacio ? '' : visible}
          placeholder={visible}
          textStyle={num}
          isDisabled={isDisabled}
          onPress={() => setAbierto(true)}
          data-testid={testId}
        />
      ) : (
        <Button
          id={id}
          variant="bare"
          aria-label={ariaLabel}
          isDisabled={isDisabled}
          onPress={() => setAbierto(true)}
          data-testid={testId}
          style={{
            minHeight: 46,
            padding: '0 12px',
            justifyContent: 'flex-end',
            borderRadius: radio.boton,
            border: `1px solid ${color.line2}`,
            backgroundColor: color.surface,
            ...style,
          }}
        >
          <Text
            style={{
              ...densidad.nombre,
              ...num,
              color: vacio ? color.fg4 : color.fg,
              ...estiloTexto,
            }}
          >
            {visible}
          </Text>
        </Button>
      )}
      {abierto && (
        <HojaImporte
          titulo={nombre}
          valor={valor}
          opcional={opcional}
          conSigno={conSigno}
          testId={testId}
          onCerrar={() => setAbierto(false)}
          onCambiar={centimos => {
            setAbierto(false);
            if (centimos !== valor) {
              onCambiar(centimos);
            }
          }}
        />
      )}
    </>
  );
}

function HojaImporte({
  titulo,
  valor,
  opcional,
  conSigno,
  testId,
  onCerrar,
  onCambiar,
}: {
  titulo: string;
  valor: number | null;
  opcional: boolean;
  conSigno: boolean;
  testId?: string;
  onCerrar: () => void;
  onCambiar: (centimos: number | null) => void;
}) {
  const { t } = useTranslation();
  const format = useFormat();
  const { escrito, centimos, sustituir, pulsar } = useImporteTecleado(valor);
  const [negativo, setNegativo] = useState(conSigno && (valor ?? 0) < 0);
  const hecho = () =>
    onCambiar(
      escrito === '' && opcional ? null : negativo ? -centimos : centimos,
    );

  return (
    <Hoja
      aria-label={titulo}
      data-testid={testId ? `${testId}-hoja` : undefined}
      onCerrar={onCerrar}
    >
      <View style={{ padding: '4px 16px 16px', gap: 12 }}>
        <Text style={{ ...texto.titulo, color: color.fg, textAlign: 'center' }}>
          {titulo}
        </Text>
        <View style={{ alignItems: 'center' }}>
          <Text
            data-testid={testId ? `${testId}-cifra` : undefined}
            style={{
              ...num,
              fontSize: 40,
              fontWeight: 800,
              letterSpacing: '-0.03em',
              padding: '0 10px',
              borderRadius: 12,
              color: escrito === '' ? color.fg3 : color.fg,
              backgroundColor: sustituir ? color.accentSoft : 'transparent',
            }}
          >
            {negativo ? '−' : ''}
            {escrito === ''
              ? format(0, 'financial')
              : `${escrito} ${format.currency.symbol}`}
          </Text>
        </View>
        <TecladoImporte onTecla={pulsar} testId={testId} />
        <View style={{ flexDirection: 'row', gap: 8 }}>
          {conSigno && (
            <Boton
              variante="fantasma"
              aria-label={t('Change sign')}
              aria-pressed={negativo}
              data-testid={testId ? `${testId}-signo` : undefined}
              onPress={() => setNegativo(n => !n)}
              style={{ minWidth: 64, fontSize: 18 }}
            >
              −/+
            </Boton>
          )}
          {opcional && (
            <Boton
              variante="fantasma"
              onPress={() => onCambiar(null)}
              style={{ flex: 1 }}
            >
              <Trans>Leave empty</Trans>
            </Boton>
          )}
          <Boton
            bloque={!opcional && !conSigno}
            onPress={hecho}
            data-testid={testId ? `${testId}-hecho` : undefined}
            style={opcional || conSigno ? { flex: 1 } : undefined}
          >
            <Trans>Done</Trans>
          </Boton>
        </View>
      </View>
    </Hoja>
  );
}
